/* eslint-disable react/no-children-prop */
import { MainNavbar } from 'components';
import { getUnAuthEventBySlug } from 'apis/events';
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { calculateTotalHours } from 'shared/dates';
import { capitalize } from 'shared/textConverter';
import { formatDate } from 'utils/dataConversion';
import { decodeFromBase64, encodeToBase64 } from 'utils/encodeAndDecode';

// Assets
import clock from 'assets/DetailPage/Clock.svg';
import ageIcon from 'assets/DetailPage/ageIcon.svg';
import frame2 from 'assets/DetailPage/frame2.svg';
import frame3 from 'assets/DetailPage/frame3.svg';
import singlesGray from 'assets/DetailPage/singlesGray.svg';
import avatar from 'assets/icons/avatar.png';

const Description = lazy(() => import("./components/description"));
const Comments = lazy(() => import("./components/comments"));
const TermsAndConditions = lazy(() => import("./components/termsAndConditions"));

function Index() {
  const { slug, comment } = useParams();
  const [data, setData] = useState(null);
  const [bookings, setBooking] = useState(null);
  const [currentTab, setCurrentTab] = useState(comment === 'true' ? 'comments' : 'description');
  const commentSectionRef = useRef(null);
  const navigate = useNavigate();

  const handleHostClick = () => {
    const hostId = data?.host_profile?.hostId;
    if (!hostId) return;

    navigate(`/event/host/details/${encodeToBase64(hostId)}`);
  };

  const fetchEventBySlug = async () => {
    try {
      const res = await getUnAuthEventBySlug(decodeFromBase64(slug));
      const { event, ...restData } = res?.data ?? {};
      setBooking(restData);
      setData(event);
    } catch (error) {
      console.error('Failed to fetch event:', error);
    }
  };

  useEffect(() => {
    fetchEventBySlug();
  }, [slug]);

  useEffect(() => {
    if (comment === 'true' && currentTab === 'comments' && commentSectionRef.current) {
      // Small delay ensures DOM is ready before scroll
      setTimeout(() => {
        commentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [comment, currentTab, data]);

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center h-full bg-white min-h-screen">
      {/* Navbar Section */}
      <div className="w-full">
        <MainNavbar />
      </div>

      <div className="w-full px-4 lg:px-0 lg:w-10/12 mx-auto mt-6">
        <h1 className="text-start text-black font-medium text-2xl md:text-4xl mb-6">
          {capitalize(data?.title)}
        </h1>

        {/* Header Section: Image + Details */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
          {/* Main Image */}
          <div className="relative w-full lg:w-7/12 h-[440px] overflow-hidden rounded-lg">
            <span
              className={`absolute z-10 px-2 py-1 text-sm font-semibold text-white rounded-md top-3 left-3 ${
                data?.mode === 'offline' ? 'bg-brandRed' : 'bg-green-500'
              }`}
            >
              {data?.mode === 'offline' ? 'Offline' : 'Online'}
            </span>
            <img
              className="w-full h-full object-cover"
              src={data?.image}
              alt={data?.title || "Event banner"}
            />
          </div>

          {/* Event Details Card */}
          <div className="flex items-center w-full lg:w-5/12 min-h-[440px] border border-gray-400 rounded text-black p-4">
            <div className="w-full">
              <h2 className="text-xl font-medium mb-4">{data?.title}</h2>

              <div className="text-3xl font-semibold mb-5">
                {data?.isFree ? 'Free' : `₹ ${data?.price}`}
              </div>

              <div className="w-full h-14 bg-brandRed text-white text-sm font-bold flex items-center justify-center rounded cursor-pointer mb-5">
                Book
              </div>

              <div className="flex flex-col space-y-3">
                {/* Duration */}
                <p className="flex items-center">
                  <img src={frame3} alt="Duration" className="me-2 w-5 h-5" />
                  {calculateTotalHours(
                    data?.start_date,
                    data?.start_time,
                    data?.end_date,
                    data?.end_time
                  )}{' '}
                  Hr
                </p>

                {/* Relationship Status */}
                <p className="flex items-center">
                  <img
                    src={data?.relationship_status === 'single' ? singlesGray : frame2}
                    alt="Relationship"
                    className="me-2 w-5 h-5"
                  />
                  {capitalize(data?.relationship_status)}
                </p>

                {/* Date Range */}
                <p className="flex items-center">
                  <img src={clock} alt="Date" className="me-2 w-5 h-5" />
                  {formatDate(data?.start_date)} – {formatDate(data?.end_date)}
                </p>

                {/* Age Range */}
                <p className="flex items-center mt-2">
                  <img src={ageIcon} alt="Age" className="me-2 w-5 h-5" />
                  {data?.age_from} – {data?.age_to}
                </p>
              </div>
              {data?.host_profile && (
                <button
                  type="button"
                  onClick={handleHostClick}
                  className="w-full mt-5 p-3 border border-gray-200 rounded flex items-center gap-3 text-left hover:border-brandRed hover:bg-red-50 transition-colors"
                >
                  <img
                    src={data?.host_profile?.photo || avatar}
                    alt={data?.host_profile?.name || 'Host'}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500">Hosted by</p>
                    <p className="text-base font-semibold text-black truncate">
                      {data?.host_profile?.name || 'Event Host'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {data?.host_profile?.followers_count ?? 0} followers
                    </p>
                  </div>
                  <span className="text-xl text-gray-400">{'>'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs & Content */}
        <div
          ref={commentSectionRef}
          className="mt-10 w-full"
        >
          <div className="flex flex-col gap-6">
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-4">
              {comment === 'false' && (
                <button
                  type="button"
                  className={`font-medium text-base pb-2 border-b-2 ${
                    currentTab === 'description'
                      ? 'text-black border-black'
                      : 'text-gray-500 border-transparent'
                  }`}
                  onClick={() => setCurrentTab('description')}
                >
                  Description
                </button>
              )}

              {data?.enable_comments && (
                <button
                  type="button"
                  className={`font-medium text-base pb-2 border-b-2 ${
                    currentTab === 'comments'
                      ? 'text-black border-black'
                      : 'text-gray-500 border-transparent'
                  }`}
                  onClick={() => setCurrentTab('comments')}
                >
                  Comments
                </button>
              )}

              {comment === 'false' && (
                <button
                  type="button"
                  className={`font-medium text-base pb-2 border-b-2 ${
                    currentTab === 'Terms_and_conditions'
                      ? 'text-black border-black'
                      : 'text-gray-500 border-transparent'
                  }`}
                  onClick={() => setCurrentTab('Terms_and_conditions')}
                >
                  Terms And Conditions
                </button>
              )}
            </div>

            {/* Tab Content */}
            <div className="pb-10">
              {currentTab === 'description' && comment === 'false' && (
                <Suspense fallback={<div className="py-4">Loading description...</div>}>
                  <Description data={data} />
                </Suspense>
              )}

              {currentTab === 'comments' && (
                <Suspense fallback={<div className="py-4">Loading comments...</div>}>
                  <Comments
                    data={data}
                    isEdit={comment === 'false' ? false : decodeFromBase64(slug)}
                  />
                </Suspense>
              )}

              {currentTab === 'Terms_and_conditions' && comment === 'false' && (
                <Suspense fallback={<div className="py-4">Loading terms...</div>}>
                  <TermsAndConditions data={bookings?.event_terms_and_conditions} />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Index;
